import React, { useState, useEffect } from "react";
import { getCourseDetail } from "PlatForms/OrgLMS/Services/AdminServices/CourseListService";
import { Form, message } from "antd";
import api from "Common/Client/Interceptor";

const useCoursesDetail = (id, contentType, form) => {
  const [getCourses, setGetCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileType, setFileType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const resetModalState = () => {
    form.resetFields();
    setFileList([]);
    setFileType(null);
  };

  useEffect(() => {
    fetchCourses();
  }, [id]);

  const fetchCourses = async () => {
    try {
      const data = await getCourseDetail(id);
      setGetCourses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const allCourseTypes = [
    { label: "Quiz", value: 1 },
    { label: "QuizAnswer", value: 2 },
    { label: "Assignment", value: 3 },
    { label: "AssignmentAnswer", value: 4 },
    { label: "Content", value: 5 },
    { label: "Video", value: 6 },
  ];

  const allFileTypes = [
    { label: "PDF", value: 1 },
    { label: "Sheet", value: 2 },
    { label: "Presentation", value: 3 },
    { label: "Video", value: 4 },
  ];

  const filteredFileTypes =
    contentType === 6 ? allFileTypes.filter((type) => type.value === 4) : allFileTypes;

  const getTypeLabel = (type) => {
    const types = ["Quiz ", "QuizAnswer", "Assignment", "AssignmentAnswer", "Content", "Video"];
    return types[type - 1] || "Unknown";
  };

  // Define all possible types
  const allTypes = ["Quiz ", "QuizAnswer", "Assignment", "AssignmentAnswer", "Content", "Video"];
  // Table Columns for Course Files
  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text, record) => (
        <a
          onClick={() => downloadCourseItemFile(record.id, record.fileName)}
          style={{ cursor: "pointer", color: "#9A6B0E" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
      render: (fileType) =>
        fileType === 1
          ? "PDF"
          : fileType === 2
          ? "Sheet"
          : fileType === 3
          ? "Presentation"
          : fileType === 4
          ? "Video"
          : "Unknown",
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const downloadCourseItemFile = async (courseItemId, fileName) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(
        `/Library/Download-CourseItem-File?courseItemId=${courseItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      message.error("Failed");
    }
  };

  const handleSubmit = async (values) => {
    try {
      setUploading(true);
      const token = localStorage.getItem("token") || "";
      // Create FormData object for multipart/form-data
      const formData = new FormData();
      formData.append("Title", values.title);
      formData.append("CourseId", id);
      formData.append("ContentType", values.contentType);
      formData.append("FileType", values.fileType);

      // Only append file if one was selected
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("FileAttachment", fileList[0].originFileObj);
      }

      const response = await api.post("/Library/AddCourseItem", formData, {
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        message.success("Course item added successfully!");
        fetchCourses();
        setIsModalOpen(false);
        form.resetFields();
        setFileList([]);
        setFileType(null);
      } else {
        throw new Error("Failed to add course item");
      }
    } catch (error) {
      console.error("Error adding course item:", error);
    } finally {
      setUploading(false);
    }
  };

  return {
    fileType,
    isModalOpen,
    uploading,
    allCourseTypes,
    filteredFileTypes,
    allTypes,
    columns,
    getTypeLabel,
    loading,
    getCourses,
    setIsModalOpen,
    setFileList,
    handleSubmit,
    setFileType,
    fileList,
  };
};

export default useCoursesDetail;
